import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LabelPaymentStatus, PartnerPaymentSendCashback, PaymentStatus } from '@models/partner/payments/partner-payment';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { PartnerPayamentsService } from '@services/partner-payments.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTimeWithSeconds } from '@utils/utils';
import { ToastContainerDirective } from 'ngx-toastr';
import { GridReadyEvent } from 'ag-grid-community';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-partner-payments',
  templateUrl: './partner-payments.component.html',
  styleUrls: ['./partner-payments.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class PartnerPaymentsComponent implements OnInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalPartnerPayout') modalPartnerPayout;
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;

  tableColumnsDefs;
  tableGridOptions;
  tableLoading: boolean;
  tableRowData = null;
  paginationPageSize = 10;

  totalCredit;

  modalRefPayout: NgbModalRef;
  private stringEmpty = '';

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  gridAnimationState = 'hide';

  constructor(
    private paymentService: PartnerPayamentsService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private atlasToast: AtlasToastService
  ) {}

  ngOnInit(): void {
    this.loaderService.show();
    const that = this;
    this.setTotalCredit();
    this.createTable();
    this.gridOptionDefinition(that);
  }

  private setTotalCredit() {
    this.paymentService.getTotalProfit().subscribe(totalProfit => {
      this.totalCredit = totalProfit.totalProfit;
    });
  }

  private gridOptionDefinition(that) {
    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      // rowHeight: 110,
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that)
    });
  }

  private reloadList(startRow?, endRow?, sort?) {
    return this.paymentService
      .listCashbackHistory(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? this.getColId(sort[0].colId) : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  private getColId(id: string) {
    switch (id) {
      case 'createDate':
        return 'CREATE_TIME';
      case 'createType':
        return 'TOTAL';
    }
  }

  private createTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'common_type',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: () => {
            return this.getTranslateLabels('partner_payout_list_payments_tablecontent_type');
          }
        },
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'partner_payout_list_payments_tablefield_date',
        field: 'createDate',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            return params && params.data ? this.getCreateTimeRenderHtml(params.data.createDate) : this.stringEmpty;
          }
        },
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'partner_payout_list_payments_tablefield_total',
        field: 'total',
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'partner_payout_mode',
        field: 'paymentType',
        sortable: false,
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'partner_payout_list_payments_tablefield_status',
        field: 'paymentStatus',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            return params && params.data ? this.getLabelStatus(params.data.paymentStatus) : this.stringEmpty;
          }
        },
        cellStyle: { 'line-height': '25px' },
        width: 200
      }
    ];
  }

  openModalPayout() {
    this.modalRefPayout = this.modalService.open(this.modalPartnerPayout, { size: 'lg' });
    this.modalRefPayout.result.then(
      result => {},
      reason => {
        this.modalPayoutClose();
      }
    );
  }

  modalPayoutSend(value) {
    const sendCashback = PartnerPaymentSendCashback.deserialize(value);
    this.callSendCashBackService(sendCashback);
    this.modalPayoutClose();
  }

  modalPayoutClose() {
    this.modalRefPayout.close();
  }

  // private method

  private callSendCashBackService(sendCashback: PartnerPaymentSendCashback) {
    this.paymentService.sendCashbackMail(sendCashback).subscribe(
      success => this.successResultCallSendCashback(),
      error => this.errorResultCallSendCashback()
    );
  }

  private successResultCallSendCashback() {
    this.atlasToast.success('common_success', null, 6000, this.toastContainer);
    this.grid.refresh();
  }

  private errorResultCallSendCashback() {
    this.atlasToast.error('common_error', null, 6000, this.toastContainer);
    this.grid.refresh();
  }

  private getCreateTimeRenderHtml(createTime) {
    const dateTime = formateDateTimeWithSeconds(createTime).split(' ');
    const date = dateTime[0];
    const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
    return '<div class="dateTime"><span>' + date + '</span> <span>' + time + '</span></div>';
  }

  private getLabelStatus(status) {
    switch (status) {
      case PaymentStatus.REQUESTED:
        return this.getTranslateLabels(LabelPaymentStatus.REQUESTED);
      case PaymentStatus.ACCEPTED:
        return this.getTranslateLabels(LabelPaymentStatus.ACCEPTED);
      case PaymentStatus.PAID:
        return this.getTranslateLabels(LabelPaymentStatus.PAID);
      case PaymentStatus.CANCELLED:
        return this.getTranslateLabels(LabelPaymentStatus.CANCELLED);
      default:
        return this.stringEmpty;
    }
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }
}
